import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from 'context/AuthContext';
import { useLocation } from 'react-router-dom';
import api from 'services/api';
import ReactMarkdown from 'react-markdown';

// ─── Suggestions contextuelles par page ───
const PAGE_SUGGESTIONS = {
  '/agents': ['Performance agents ce mois', 'Agents sans site affecte', 'Contrats expirant bientot', 'Absences ce mois'],
  '/rh': ['Performance agents ce mois', 'Agents sans site affecte', 'Contrats expirant bientot', 'Absences ce mois'],
  '/contrats': ['Contrats expirant bientot', 'Agents en periode d\'essai', 'Renouvellements a prevoir'],
  '/sites': ['Sites a risque', 'Rondes non effectuees', 'Postes vacants aujourd\'hui', 'Couverture sites'],
  '/rondes': ['Sites a risque', 'Rondes non effectuees', 'Performance patrouilles', 'Incidents recents'],
  '/incidents': ['Resume incidents du mois', 'Sites les plus touches', 'Incidents critiques ouverts', 'Tendance incidents'],
  '/facturation': ['Factures impayees', 'Chiffre d\'affaires du mois', 'Clients en retard de paiement', 'Previsions recettes'],
  '/comptabilite': ['Sante financiere', 'Charges du mois', 'Resultat net', 'Tresorerie'],
  '/devis': ['Devis en attente', 'Taux conversion devis', 'Montant devis du mois'],
  '/telesurveillance': ['Alertes TSV recentes', 'Sites sous telesurveillance', 'Performance operateurs'],
  default: ['Resume de la semaine', 'Sites a risque', 'Performance agents', 'Sante financiere', 'Generer rapport', 'Postes vacants', 'Incidents du jour'],
};

function getSuggestions(pathname) {
  for (const [key, val] of Object.entries(PAGE_SUGGESTIONS)) {
    if (key !== 'default' && pathname.includes(key)) return val;
  }
  return PAGE_SUGGESTIONS.default;
}

// ─── Horodatage ───
function formatTimestamp(date) {
  const now = new Date();
  const d = new Date(date);
  const h = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = (today - msgDay) / 86400000;
  if (diff === 0) return `Aujourd'hui a ${h}`;
  if (diff === 1) return `Hier a ${h}`;
  return `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} a ${h}`;
}

const TypingDots = () => (
  <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '4px 0' }}>
    <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#8B0000', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700, fontFamily: "'Georgia', 'Times New Roman', serif", flexShrink: 0 }}>IA</div>
    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '12px 12px 12px 2px', padding: '10px 14px', display: 'flex', gap: 4, alignItems: 'center' }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#8B0000', display: 'inline-block', animation: `typingDot 1.2s infinite ${i * 0.2}s` }} />
      ))}
    </div>
    <style>{`@keyframes typingDot { 0%, 60%, 100% { opacity: 0.3; transform: translateY(0); } 30% { opacity: 1; transform: translateY(-3px); } }`}</style>
  </div>
);

const MD_COMPONENTS = {
  h1: ({ children }) => <div style={{ fontSize: 15, fontWeight: 700, color: '#8B0000', margin: '8px 0 4px' }}>{children}</div>,
  h2: ({ children }) => <div style={{ fontSize: 14, fontWeight: 700, color: '#8B0000', margin: '8px 0 4px' }}>{children}</div>,
  h3: ({ children }) => <div style={{ fontSize: 13, fontWeight: 700, color: '#8B0000', margin: '6px 0 3px' }}>{children}</div>,
  strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
  ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: 18 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: 18 }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: 2, lineHeight: 1.5 }}>{children}</li>,
  hr: () => <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '8px 0' }} />,
  p: ({ children }) => <p style={{ margin: '4px 0', lineHeight: 1.5 }}>{children}</p>,
  code: ({ children }) => <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3, fontSize: 11 }}>{children}</code>,
};

const ThemeButton = ({ label, onClick }) => (
  <button
    onClick={onClick}
    onMouseEnter={e => { e.currentTarget.style.background = '#8B0000'; e.currentTarget.style.color = '#fff'; }}
    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#8B0000'; }}
    style={{
      padding: '4px 8px', fontSize: 10, background: '#fff', border: '1px solid #8B0000',
      borderRadius: 12, cursor: 'pointer', color: '#8B0000', fontWeight: 500,
      transition: 'background 0.15s, color 0.15s', textAlign: 'center',
      height: 24, lineHeight: 1, whiteSpace: 'nowrap',
    }}
  >{label}</button>
);

export default function ChatbotIA() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [iaConnected, setIaConnected] = useState(null);
  const [iaModel, setIaModel] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);
  const [copied, setCopied] = useState(false);
  const chatRef = useRef();
  const inputRef = useRef();

  const roles = user?.roles || [];
  const canSee = roles.some(r => ['dg', 'admin'].includes(r));

  const suggestions = getSuggestions(location.pathname);

  // Check IA status on mount
  useEffect(() => {
    if (!canSee) return;
    api.get('/ia/stats').then(r => {
      setIaConnected(true);
      // ⚠️ LA VALEUR VIENT DE LA REPONSE, JAMAIS D'UNE CONSTANTE D'ECRAN.
      //    Avant : `m.includes('haiku') ? 'Haiku 4.5' : m.includes('sonnet') ? 'Sonnet 4' : m`
      //    — deux noms de modele ECRITS EN DUR ICI. Le jour ou AI_MODEL_FAST
      //    change dans le .env, l'ecran aurait continue d'afficher l'ancien
      //    libelle : un correspondance codee en dur finit toujours par mentir,
      //    et elle ment sans bruit puisque la pastille reste verte.
      //    ➡️ On rend `modelFast` TEL QU'IL VIENT. L'ecran suit la donnee,
      //       personne ne touche au code. (Prod au 13/09/2026 :
      //       AI_MODEL_FAST=claude-haiku-4-5-20251001.)
      setIaModel(r.data.modelFast || '');
    }).catch(() => setIaConnected(false));
  }, [canSee]);

  const send = async (text) => {
    const q = text || input.trim();
    if (!q || loading) return;

    const ts = new Date().toISOString();
    setMessages(prev => [...prev, { role: 'user', content: q, ts }]);
    setInput('');
    setLoading(true);

    try {
      const r = await api.post('/ia/chat', { question: q });
      setMessages(prev => [...prev, { role: 'assistant', content: r.data.reponse, model: r.data.model, fromCache: r.data.fromCache, ts: new Date().toISOString() }]);
      setIaConnected(true);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'error', content: err.response?.data?.error || 'Erreur IA', ts: new Date().toISOString() }]);
      if (err.response?.status === 503) setIaConnected(false);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const clearChat = () => {
    if (!confirmClear) { setConfirmClear(true); setTimeout(() => setConfirmClear(false), 3000); return; }
    setMessages([]);
    setConfirmClear(false);
  };

  // Copie le DERNIER rapport (message assistant courant), nettoye du markdown.
  const handleCopy = useCallback(async () => {
    const last = [...messages].reverse().find(m => m.role === 'assistant');
    const text = last ? last.content.replace(/[#*_`]/g, '').trim() : '';
    if (!text) return;
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* presse-papier indisponible */ }
  }, [messages]);

  // Impression ciblee : classe body scopee -> la CSS @media print n'affecte QUE le rapport.
  const handlePrint = useCallback(() => {
    document.body.classList.add('chatbot-printing');
    const cleanup = () => { document.body.classList.remove('chatbot-printing'); window.removeEventListener('afterprint', cleanup); };
    window.addEventListener('afterprint', cleanup);
    window.print();
  }, []);

  const exportPDF = useCallback(async () => {
    try {
      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      const now = new Date();
      const dateStr = now.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');

      doc.setFontSize(16);
      doc.setTextColor(139, 0, 0);
      doc.text('GSS-360 — Conversation IA', 14, 20);
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(`Exporte le ${now.toLocaleString('fr-FR')}`, 14, 27);

      let y = 36;
      doc.setFontSize(10);
      for (const m of messages) {
        if (y > 270) { doc.addPage(); y = 20; }
        const label = m.role === 'user' ? 'Vous' : m.role === 'error' ? 'Erreur' : 'GSS-360 IA';
        doc.setTextColor(m.role === 'user' ? 0 : 139, 0, 0);
        doc.setFont(undefined, 'bold');
        doc.text(`${label} — ${formatTimestamp(m.ts)}`, 14, y);
        y += 5;
        doc.setFont(undefined, 'normal');
        doc.setTextColor(50);
        const lines = doc.splitTextToSize(m.content.replace(/[#*_`]/g, ''), 180);
        for (const line of lines) {
          if (y > 280) { doc.addPage(); y = 20; }
          doc.text(line, 14, y);
          y += 4.5;
        }
        y += 4;
      }

      doc.save(`GSS360_Chat_${dateStr}.pdf`);
    } catch {
      alert('Export PDF non disponible');
    }
  }, [messages]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, loading]);

  if (!canSee) return null;

  const statusDot = iaConnected === null ? '#999' : iaConnected ? '#22C55E' : '#DC2626';

  return (
    <>
      <style>{`
        @media print {
          body.chatbot-printing * { visibility: hidden !important; }
          body.chatbot-printing .chatbot-panel { position: static !important; transform: none !important; width: 100% !important; max-height: none !important; height: auto !important; overflow: visible !important; box-shadow: none !important; border: none !important; }
          body.chatbot-printing .chatbot-panel, body.chatbot-printing .chatbot-print-area, body.chatbot-printing .chatbot-print-area * { visibility: visible !important; }
          body.chatbot-printing .chatbot-print-area { overflow: visible !important; max-height: none !important; }
          body.chatbot-printing .chatbot-no-print { display: none !important; }
        }
      `}</style>
      {!open && (
        <button onClick={() => setOpen(true)} style={{
          position: 'fixed', bottom: 24, right: 24, width: 56, height: 56,
          borderRadius: '50%', border: '2px solid #C9A96E', cursor: 'pointer', zIndex: 9999,
          background: '#8B0000', color: '#fff', fontSize: 20, fontWeight: 700,
          fontFamily: "'Georgia', 'Times New Roman', serif",
          boxShadow: '0 4px 20px rgba(139,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = '#A01010'}
        onMouseLeave={e => e.currentTarget.style.background = '#8B0000'}
        >IA</button>
      )}

      {open && (
        <div className="chatbot-panel" style={{
          position: 'fixed',
          ...(expanded
            ? { top: '5%', bottom: '5%', left: '50%', transform: 'translateX(-50%)', width: 'min(90vw, 900px)', maxHeight: '90vh' }
            : { bottom: 80, right: 24, width: 'min(380px, calc(100vw - 48px))', maxHeight: 'calc(100vh - 120px)' }),
          background: '#fff', borderRadius: 12, zIndex: 9998,
          boxShadow: '0 8px 40px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column',
          overflow: 'hidden', border: '1px solid #e0e0e0',
          transition: 'box-shadow 0.2s ease',
        }}>
          {/* Header */}
          <div className="chatbot-no-print" style={{
            padding: '10px 12px', background: '#8B0000',
            color: '#fff', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}>
            {/* Monogramme neutre : le « G » servi a tous les tenants etait
                celui de l'editeur. Meme regle que le prompt systeme et que le
                logo des PDF — aucune identite d'editeur chez un client. */}
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, fontFamily: "'Georgia', 'Times New Roman', serif" }}>IA</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Assistant GSS-360</div>
              <div style={{ fontSize: 10, opacity: 0.8, display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusDot, display: 'inline-block' }} />
                Intelligence IA
                {/* ⚠️ LE CABLAGE S'ARRETAIT UNE LIGNE TROP TOT. `iaModel` etait
                    calcule depuis /ia/stats et n'etait JAMAIS affiche : la
                    pastille de statut avait un frere qui ne sortait pas. C'est
                    ce que l'avertissement `no-unused-vars` signalait — pas du
                    code mort, une fonctionnalite deposee.
                    ⓘ `title` porte la valeur entiere : l'identifiant complet
                      peut etre long, on le tronque a l'oeil sans le perdre. */}
                {iaModel && (
                  <span
                    title={iaModel}
                    style={{
                      opacity: 0.75, maxWidth: 150, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    · {iaModel}
                  </span>
                )}
              </div>
            </div>
            {/* Action buttons */}
            <button onClick={clearChat} title={confirmClear ? 'Confirmer effacement' : 'Effacer conversation'} style={{ background: confirmClear ? 'rgba(255,255,255,0.3)' : 'none', border: 'none', color: '#fff', fontSize: 14, cursor: 'pointer', opacity: 0.8, padding: '2px 6px', borderRadius: 4 }}>
              {confirmClear ? 'Effacer ?' : '\u{1F5D1}'}
            </button>
            {/* Actions rapport : uniquement en mode agrandi (mobile-first : header compact allege) */}
            {expanded && messages.length > 0 && (
              <>
                <button onClick={handleCopy} title={copied ? 'Copie !' : 'Copier le rapport'} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 14, cursor: 'pointer', opacity: 0.8, padding: '2px 6px' }}>
                  {copied ? '\u{2713}' : '\u{1F4CB}'}
                </button>
                <button onClick={handlePrint} title="Imprimer / Enregistrer en PDF" style={{ background: 'none', border: 'none', color: '#fff', fontSize: 14, cursor: 'pointer', opacity: 0.8, padding: '2px 6px' }}>
                  {'\u{1F5A8}'}
                </button>
                <button onClick={exportPDF} title="Exporter en PDF" style={{ background: 'none', border: 'none', color: '#fff', fontSize: 14, cursor: 'pointer', opacity: 0.8, padding: '2px 6px' }}>
                  {'\u{1F4C4}'}
                </button>
              </>
            )}
            <button onClick={() => setExpanded(e => !e)} title={expanded ? 'Reduire' : 'Agrandir'} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 14, cursor: 'pointer', opacity: 0.8, padding: '2px 6px' }}>
              {expanded ? '\u{2747}' : '\u{26F6}'}
            </button>
            <button onClick={() => { setOpen(false); setExpanded(false); }} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', opacity: 0.8, lineHeight: 1, padding: 4 }}>&times;</button>
          </div>

          {/* Messages */}
          <div ref={chatRef} className="chatbot-print-area" style={{ flex: 1, overflowY: 'auto', padding: 12, background: '#f8f8f8', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {messages.length === 0 && (
              <div style={{ padding: '12px 4px' }}>
                <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#8B0000', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700, fontFamily: "'Georgia', 'Times New Roman', serif", flexShrink: 0 }}>IA</div>
                  <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '12px 12px 12px 2px', padding: '10px 14px', fontSize: 12, lineHeight: 1.6, color: '#333' }}>
                    Bonjour ! Je suis votre assistant GSS-360. Posez-moi vos questions sur vos agents, sites, incidents ou finances.
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                  {suggestions.map((q, i) => (
                    <ThemeButton key={i} label={q} onClick={() => send(q)} />
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i}>
                <div style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', gap: 6 }}>
                  {m.role !== 'user' && (
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: m.role === 'error' ? '#DC2626' : '#8B0000', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700, fontFamily: "'Georgia', 'Times New Roman', serif", flexShrink: 0 }}>
                      {m.role === 'error' ? '!' : 'G'}
                    </div>
                  )}
                  <div style={{
                    maxWidth: '80%', padding: '8px 12px', fontSize: 12, lineHeight: 1.5,
                    borderRadius: m.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                    background: m.role === 'user' ? '#8B0000' : m.role === 'error' ? '#FEF2F2' : '#fff',
                    color: m.role === 'user' ? '#fff' : m.role === 'error' ? '#DC2626' : '#333',
                    border: m.role === 'user' ? 'none' : m.role === 'error' ? '1px solid #FECACA' : '1px solid #e0e0e0',
                  }}>
                    {m.role === 'assistant' ? (
                      <div className="chatbot-md">
                        <ReactMarkdown components={MD_COMPONENTS}>{m.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <span style={{ whiteSpace: 'pre-wrap' }}>{m.content}</span>
                    )}
                    {m.fromCache && <span style={{ fontSize: 9, color: '#aaa', display: 'block' }}>(cache)</span>}
                  </div>
                </div>
                {/* Timestamp */}
                {m.ts && (
                  <div style={{ textAlign: m.role === 'user' ? 'right' : 'left', paddingLeft: m.role !== 'user' ? 34 : 0, fontSize: 10, color: '#aaa', marginTop: 2 }}>
                    {formatTimestamp(m.ts)}
                  </div>
                )}
              </div>
            ))}
            {loading && <TypingDots />}
          </div>

          {/* Input */}
          <div className="chatbot-no-print" style={{ borderTop: '1px solid #eee', padding: 8, display: 'flex', gap: 6, flexShrink: 0, background: '#fff' }}>
            <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey}
              placeholder="Votre question..." style={{
                flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: 8,
                fontSize: 12, outline: 'none',
              }} />
            <button onClick={() => send()} disabled={loading || !input.trim()} style={{
              padding: '8px 14px', background: '#8B0000', color: '#fff', border: 'none',
              borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, opacity: loading || !input.trim() ? 0.5 : 1,
            }}>Envoyer</button>
          </div>
        </div>
      )}
    </>
  );
}

export function useAlertCount() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    api.get('/notifications/count').then(res => setCount(res.data.non_lues || 0)).catch(() => {});
  }, []);
  return count;
}
